import { BaseApiService } from './base.service';
import { queryClient } from '@/shared/lib/query-client';
import { cacheKeys } from '@/shared/lib/cache-invalidation';
import { deriveIsGuestMode } from '@/features/subscription/model/active-subscription.query';
import type { ActiveSubscriptionData } from '@/features/subscription/model/active-subscription.query';
import type { SubscriptionResource } from '@/types/subscription';
import type { ProfileData, ProfileEffectivePlan } from '@/types/profile';
import type { User } from '@/types/user';
import { setAnalyticsCollectionForUser } from '@/shared/lib/analytics';

interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
}

class ProfileService extends BaseApiService {
  constructor() {
    super();
  }

  /**
   * Fetch user profile with subscription data
   */
  async fetchProfile(): Promise<ProfileData> {
    const response = await this.httpGet<
      ApiResponse<{ id: string; attributes: ProfileData }>
    >('/v1/user', undefined, {
      loading: { trackEndpoint: 'profile.fetch' },
      // 403 here is not a failure, it IS the answer: the address is not
      // confirmed yet. Auth init deliberately probes this endpoint to discover
      // that state, so shouting it into the console (and Sentry) on every boot
      // of an unverified account described a working flow as an incident.
      // Any other status stays loud.
      error: { expectedStatuses: [403] },
    });

    const { id, attributes } = response.data;

    // Merge top-level id into user object (JSON:API spec: id is not in attributes)
    return {
      ...attributes,
      user: attributes.user ? { ...attributes.user, id } : undefined,
    };
  }

  /**
   * Refresh user profile and update every cache that derives from it.
   * Used for reconnection sync and forced updates. Auth flows use the same
   * pipeline (applyProfileData) below — same seeding for /user and login.
   */
  async refreshProfile(): Promise<ProfileData> {
    const profileData = await this.fetchProfile();
    await this.applyProfileData(profileData, 'profile-refresh');
    return profileData;
  }

  /**
   * Single source-of-truth seeder for caches that derive from a
   * UserProfileResource payload (User entity + subscription + token balance).
   *
   * `source` is kept for log-line continuity across auth flows: `'login'` /
   * `'register'` / `'google-auth'` / `'verify-email'` / `'profile-refresh'`.
   */
  async applyProfileData(profileData: ProfileData, _source: string): Promise<void> {
    // User entity — server-owned, lives in the Query cache (Wave A3 +
    // STATE_POLICY). Merge into any existing cache entry so WS deltas + auth
    // seeds share the same write channel. Token balance lives at its own
    // top-level key (`cacheKeys.balance`); the `tokens_balance` field on User
    // is the snapshot from the auth payload, kept consistent below.
    const incomingUser = this.extractUser(profileData);
    if (incomingUser) {
      queryClient.setQueryData<User>(cacheKeys.user, (prev) =>
        prev ? { ...prev, ...incomingUser } : incomingUser
      );
      // Keep internal QA/team traffic out of GA. Set unconditionally so a
      // non-test user signing in on the same browser re-enables collection
      // after a prior test session.
      setAnalyticsCollectionForUser(incomingUser.is_test ?? false);
    }

    // Effective plan + subscription always seed the cache, even for guests:
    // the server resolves `plan` to the real subscription plan or the guest
    // plan, so paywall preflight and feature gates work for both.
    if (profileData.plan || profileData.subscription) {
      queryClient.setQueryData(
        cacheKeys.subscriptions.active,
        this.transformSubscriptionData(profileData)
      );
    }

    // First-run wizard state. Only written when the payload carries it, so a
    // response from an older API (or a partial one) never erases a known state.
    if (profileData.first_run !== undefined) {
      queryClient.setQueryData(cacheKeys.firstRun, profileData.first_run);
    }

    // Sign-in methods + suggest/require flags (ADR-001 §5), same rule.
    if (profileData.sign_in !== undefined) {
      queryClient.setQueryData(cacheKeys.signIn, profileData.sign_in);
    }

    // Token balance: server-owned scalar, lives in the Query cache. WS handler
    // pushes deltas through the same key, so auth seed + WS update share one
    // write channel.
    if (profileData.tokens?.current_balance !== undefined) {
      queryClient.setQueryData(
        cacheKeys.balance,
        profileData.tokens.current_balance
      );
    }
  }

  /**
   * Build a canonical `User` from the wire-level `ProfileData.user` so every
   * auth/WS path produces an identically-shaped cache entry. Returns null
   * when the payload doesn't carry a user (e.g. logout-style responses).
   */
  extractUser(profileData: ProfileData): User | null {
    const u = profileData.user;
    if (!u) return null;
    const tokensBalance = profileData.tokens?.current_balance;
    return {
      id: u.id,
      email: u.email,
      ...(u.name !== undefined ? { name: u.name } : {}),
      ...(u.locale !== undefined ? { locale: u.locale } : {}),
      email_verified_at: u.email_verified_at ?? null,
      created_at: u.created_at,
      updated_at: u.updated_at ?? u.created_at,
      ...(u.is_email_verified !== undefined
        ? { is_email_verified: u.is_email_verified }
        : {}),
      ...(u.is_test !== undefined ? { is_test: u.is_test } : {}),
      ...(u.has_completed_renders !== undefined
        ? { has_completed_renders: u.has_completed_renders }
        : {}),
      ...(u.should_request_feedback !== undefined
        ? { should_request_feedback: u.should_request_feedback }
        : {}),
      ...(u.can_generate !== undefined
        ? { can_generate: u.can_generate }
        : {}),
      ...(u.account_attention !== undefined
        ? { account_attention: u.account_attention }
        : {}),
      ...(tokensBalance !== undefined ? { tokens_balance: tokensBalance } : {}),
    };
  }

  /**
   * Transform profile data for the active-subscription Query payload.
   * Returns a complete `ActiveSubscriptionData` (isGuestMode + effectivePlan
   * derived here so the Query bundle is self-contained — no follow-up
   * computation on the consumer side).
   */
  transformSubscriptionData(profileData: ProfileData): ActiveSubscriptionData {
    const { subscription, tokens, plan: effectivePlan } = profileData;

    // Extract token balance from nested structure
    const currentBalance = tokens?.current_balance ?? 0;

    const usage = {
      token_balance: currentBalance,
      subscription: subscription
        ? {
            monthly_token_allocation: subscription.plan?.token_allowance ?? 0,
            current_period_start:
              subscription.current_period_start || undefined,
            current_period_end: subscription.current_period_end || undefined,
          }
        : undefined,
    };

    // Transform subscription to have attributes structure if needed
    const transformedSubscription: SubscriptionResource | null = subscription
      ? {
          type: 'subscription',
          id: subscription.id,
          attributes: {
            id: subscription.id,
            status: subscription.status,
            current_period_start: subscription.current_period_start,
            current_period_end: subscription.current_period_end,
            is_cancel_at_period_end:
              subscription.is_cancel_at_period_end ?? false,
            is_pending_cancellation:
              subscription.is_pending_cancellation ?? false,
            will_renew: subscription.will_renew ?? false,
            has_billing_issue: subscription.has_billing_issue ?? false,
            grants_access: subscription.grants_access ?? false,
            is_iap_provider: subscription.is_iap_provider ?? false,
            provider: subscription.provider,
            plan_id: subscription.plan?.id ?? '',
            provider_subscription_id:
              subscription.provider_subscription_id ?? '',
            price: subscription.price ?? 0,
            created_at: subscription.created_at ?? new Date().toISOString(),
            updated_at: subscription.updated_at ?? new Date().toISOString(),
          },
          relationships: {
            user: {
              type: 'user',
              id: '',
              attributes: {
                name: '',
                email: '',
                locale: '',
                is_email_verified: false,
                email_verified_at: '',
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              },
            },
            plan: {
              type: 'plan',
              id: subscription.plan?.id ?? '',
              attributes: {
                id: subscription.plan?.id ?? '',
                name: subscription.plan?.name ?? '',
                type: subscription.plan?.type ?? '',
                description: subscription.plan?.description ?? '',
                advantages: subscription.plan?.advantages ?? '',
                price: subscription.plan?.price ?? 0,
                monthly_price: subscription.plan?.monthly_price ?? 0,
                yearly_price: subscription.plan?.yearly_price ?? 0,
                token_allowance: subscription.plan?.token_allowance ?? 0,
                avg_renders: subscription.plan?.avg_renders ?? 0,
                max_renders: subscription.plan?.max_renders ?? 0,
                is_active: subscription.plan?.is_active ?? true,
                is_public: subscription.plan?.is_public ?? true,
                limit_generations_queue:
                  subscription.plan?.limit_generations_queue ?? 0,
                limit_projects: subscription.plan?.limit_projects ?? 0,
                limit_angles: subscription.plan?.limit_angles ?? 0,
                limit_references: subscription.plan?.limit_references ?? 0,
                // Uniform per-category cap of 3 (server enforces `max:3`
                // everywhere; not a paid differentiator). Default to 3 — never
                // 1 — so a subscription payload that omits the field doesn't
                // wrongly cap references at 1.
                limit_simultaneous_references:
                  subscription.plan?.limit_simultaneous_references ?? 3,
                speed_multiplier: subscription.plan?.speed_multiplier ?? 1,
                is_feature_remove_watermark:
                  subscription.plan?.is_feature_remove_watermark ?? false,
                is_feature_output_style:
                  subscription.plan?.is_feature_output_style ?? false,
                is_feature_upscale:
                  subscription.plan?.is_feature_upscale ?? false,
                is_feature_hires_render:
                  subscription.plan?.is_feature_hires_render ?? false,
                is_feature_extended_thinking:
                  subscription.plan?.is_feature_extended_thinking ?? false,
                is_white_label_allowed:
                  subscription.plan?.is_white_label_allowed ?? false,
                is_album_advanced_options_available:
                  subscription.plan?.is_album_advanced_options_available ??
                  false,
                is_album_templates_available:
                  subscription.plan?.is_album_templates_available ?? false,
                is_feature_model_selection:
                  subscription.plan?.is_feature_model_selection ?? false,
                is_feature_cost_estimate:
                  subscription.plan?.is_feature_cost_estimate ?? false,
                is_registration_enabled:
                  subscription.plan?.is_registration_enabled ?? true,
                is_purchasable: subscription.plan?.is_purchasable ?? true,
                formatted_features: subscription.plan?.formatted_features ?? [],
                created_at:
                  subscription.plan?.created_at ?? new Date().toISOString(),
                updated_at:
                  subscription.plan?.updated_at ?? new Date().toISOString(),
              },
              links: {
                self: '',
              },
            },
          },
          links: {
            self: '',
          },
        }
      : null;

    // Prefer the top-level effective plan (server-seeded for both paid and
    // guest users — always carries paywall_payloads). Fall back to the plan
    // nested inside the subscription only if the server didn't send one.
    const subscriptionPlanResource =
      transformedSubscription?.relationships?.plan ?? null;
    const planResource = effectivePlan
      ? this.buildEffectivePlanResource(effectivePlan, subscriptionPlanResource)
      : subscriptionPlanResource;

    return {
      subscription: transformedSubscription,
      plan: planResource,
      usage,
      // Prefer the user-level can_generate flag — it folds in the
      // post-signup welcome window, which has no Subscription row and was
      // being misclassified as guest by the subscription-only check.
      isGuestMode: deriveIsGuestMode(
        transformedSubscription,
        undefined,
        profileData.user?.can_generate
      ),
      // `effectivePlan` carries the raw limit bundle the server resolves
      // (real-or-guest). The richer PlanResource above is what the UI
      // actually reads; this slot exists for selectors that still ask for
      // a flat limits object.
      effectivePlan: effectivePlan
        ? {
            name: effectivePlan.name ?? '',
            slug: String(effectivePlan.type ?? ''),
            limit_projects: effectivePlan.limit_projects ?? 0,
            limit_angles: effectivePlan.limit_angles ?? 0,
            limit_references: effectivePlan.limit_references ?? 0,
            // Uniform per-category cap of 3 across ALL plans/Passes. The
            // profile `effective_plan` payload does not carry this field (it's
            // not a paid differentiator), so this is the hardcoded real cap —
            // never 1, which wrongly capped guest/Pass-only users at 1.
            limit_simultaneous_references: 3,
            limit_generations_queue: effectivePlan.limit_generations_queue ?? 0,
          }
        : null,
    };
  }

  /**
   * Merge the top-level effective plan (minimal payload — limits +
   * paywall_payloads) with the richer subscription plan fields so the store
   * ends up with the full PlanResource shape. For guests the subscription
   * plan is absent, so defaults kick in.
   */
  private buildEffectivePlanResource(
    effectivePlan: ProfileEffectivePlan,
    subscriptionPlanResource: SubscriptionResource['relationships']['plan'] | null
  ): SubscriptionResource['relationships']['plan'] {
    const baseAttrs = subscriptionPlanResource?.attributes;
    const id = String(effectivePlan.id ?? baseAttrs?.id ?? '');
    return {
      type: 'plan',
      id,
      attributes: {
        ...baseAttrs,
        id,
        name: effectivePlan.name ?? baseAttrs?.name ?? '',
        type: effectivePlan.type ?? baseAttrs?.type ?? '',
        limit_projects: effectivePlan.limit_projects ?? baseAttrs?.limit_projects ?? 0,
        limit_angles: effectivePlan.limit_angles ?? baseAttrs?.limit_angles ?? 0,
        limit_references: effectivePlan.limit_references ?? baseAttrs?.limit_references ?? 0,
        limit_generations_queue:
          effectivePlan.limit_generations_queue ?? baseAttrs?.limit_generations_queue ?? 0,
        // Feature flags come from the effective plan (server picks real vs
        // guest), so gates work for guests too. Fall back to baseAttrs only if
        // the server response predates the effective-plan feature flags.
        is_feature_remove_watermark:
          effectivePlan.is_feature_remove_watermark
          ?? baseAttrs?.is_feature_remove_watermark ?? false,
        is_feature_output_style:
          effectivePlan.is_feature_output_style
          ?? baseAttrs?.is_feature_output_style ?? false,
        is_feature_upscale:
          effectivePlan.is_feature_upscale
          ?? baseAttrs?.is_feature_upscale ?? false,
        is_feature_hires_render:
          effectivePlan.is_feature_hires_render
          ?? baseAttrs?.is_feature_hires_render ?? false,
        is_feature_extended_thinking:
          effectivePlan.is_feature_extended_thinking
          ?? baseAttrs?.is_feature_extended_thinking ?? false,
        is_feature_model_selection:
          effectivePlan.is_feature_model_selection
          ?? baseAttrs?.is_feature_model_selection ?? false,
        is_feature_cost_estimate:
          effectivePlan.is_feature_cost_estimate
          ?? baseAttrs?.is_feature_cost_estimate ?? false,
        is_white_label_allowed:
          effectivePlan.is_white_label_allowed
          ?? baseAttrs?.is_white_label_allowed ?? false,
        is_album_advanced_options_available:
          effectivePlan.is_album_advanced_options_available
          ?? baseAttrs?.is_album_advanced_options_available ?? false,
        is_album_templates_available:
          effectivePlan.is_album_templates_available
          ?? baseAttrs?.is_album_templates_available ?? false,
        paywall_payloads: effectivePlan.paywall_payloads,
      } as SubscriptionResource['relationships']['plan']['attributes'],
      links: subscriptionPlanResource?.links ?? { self: '' },
    };
  }
}

export const profileService = new ProfileService();
